import type { SessionSummaryView } from "../contracts/sessions.js";

/**
 * Client-side paging for the full conversation list. `session.list` returns the
 * agent's whole conversation set in one API response (it has no server cursor), so the
 * full list view grows a visible window in fixed steps rather than refetching.
 *
 * It stays in the browser-shared layer so the React UI and compiled tests use
 * the same pure function.
 */

/** How many additional conversations each "Show more" reveals. */
export const CONVERSATIONS_PAGE_SIZE = 20;

/** The slice of conversations to render and whether more remain past it. */
export interface ConversationPage {
  /** The leading `visibleCount`-bounded window of conversations to render. */
  visible: SessionSummaryView[];
  /** True when conversations exist beyond the visible window. */
  hasMore: boolean;
  /** Count still hidden — drives the "Show more (N)" affordance copy. */
  remaining: number;
}

/**
 * Returns the leading window of `conversations` capped at `visibleCount`.
 * A `visibleCount` at or above the total reveals everything with no remainder;
 * a non-positive `visibleCount` is clamped to zero.
 */
export function paginateConversations(
  conversations: readonly SessionSummaryView[],
  visibleCount: number,
): ConversationPage {
  const cap = Math.max(0, Math.trunc(visibleCount));
  const visible = conversations.slice(0, cap);
  const remaining = conversations.length - visible.length;
  return { visible, hasMore: remaining > 0, remaining };
}
